/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from '@mui/material';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomGrid from 'components/grids/CustomGrid';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import { SetupProject } from 'models/setup/SetupProject';
import { findDisplayProject } from 'apis/setup/SetupProject';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import {
  deletePrjSchedule,
  findPrjScheduleList,
  findPrjTargetEquipment,
} from 'apis/setup/PrjSchedule';
import {
  PrjSchedule,
  PrjScheduleCondition,
  PrjScheduleEqp,
  ViewMode,
} from 'models/setup/PrjSchedule';
import { IconButton } from 'components/buttons/IconSVG';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import PrjScheduleRegistPopup from 'pages/setup/popup/PrjScheduleRegistPopup';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { CustomDateTimePicker } from 'components/inputs/CustomDateTimepicker';
import { useCommonModal } from 'hooks/useCommonModal';
import { CalendarProps } from 'pages/setup/PrjSchedulePage';
import useSessionStore from 'stores/useSessionStore';

/**
 * @desc 셋업프로젝트 > 일정관리 > 일정 등록/조회 (목록)
 *       [신규] → 일정 등록 화면, 행 더블클릭 → 일정 수정 화면, [캘린더] → 일정 캘린더
 * @author cnsglee
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.03   cnsglee        신규 생성 (외부 개발본 elm_dev SchedulePage 이관)
 */
const PrjScheduleManagementPage = ({ setPageMode, setViewMode }: CalendarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { ensolYn, deptCd, deptNm, elmPrjId } = useSessionStore();

  const defaultCondition: PrjScheduleCondition = {
    elmPrjId: elmPrjId || '',
    wktTpCd: '',
    schdNm: '',
    excpId: ensolYn === 'Y' ? '' : deptCd,
    tgtEqpId: '',
    schdStatCd: '',
    planStDtm: '',
    planEndDtm: '',
  };

  const gridRef = useRef<any>();
  const [condition, setCondition] = useState<PrjScheduleCondition>({
    ...defaultCondition,
    elmPrjId,
  });
  const [rowData, setRowData] = useState<PrjSchedule[]>([]);
  const [isShowDetailPopup, setIsShowDetailPopup] = useState(false);
  const [selectedData, setSelectedData] = useState<PrjSchedule>({});

  // 공통코드 (WKT_TP_CD: 업무유형, SCHD_STAT_CD: 일정상태 — 공통코드 등록 필요)
  const [wktTpCodes, setWktTpCodes] = useState<Code[]>([]);
  const [statCodes, setStatCodes] = useState<Code[]>([]);
  // 참조 콤보
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<any[]>([]);
  const [tgtEqpData, setTgtEqpData] = useState<PrjScheduleEqp[]>([]);

  useEffect(() => {
    (async () => {
      setWktTpCodes((await getCommonCodeNames('SCHD_WKT_TP_CD')) || []);
      setStatCodes((await getCommonCodeNames('SCHD_STAT_CD')) || []);
      setPrjMasterData((await findDisplayProject()) || []);
      await handleSearch(defaultCondition);
    })();
  }, []);

  // 프로젝트 선택 → 협력업체/대상설비 콤보 재조회
  useEffect(() => {
    (async () => {
      if (!condition?.elmPrjId) {
        setPrjExcpData([]);
        setTgtEqpData([]);
        return;
      }
      setPrjExcpData((await findExcpByElmPrjId(condition.elmPrjId)) || []);
      setTgtEqpData((await findPrjTargetEquipment(condition.elmPrjId, condition?.excpId)) || []);
    })();
  }, [condition?.elmPrjId, condition?.excpId]);

  const optionsPrjMaster = useMemo(
    () => (prjMasterData || []).map((o) => ({ cmnCd: o.elmPrjId, cmnCdNm: o.elmPrjNm } as Code)),
    [prjMasterData]
  );
  const optionsPrjExcp = useMemo(
    () => (prjExcpData || []).map((o) => ({ cmnCd: o.excpId, cmnCdNm: o.excpNm } as Code)),
    [prjExcpData]
  );
  const optionsTgtEqp = useMemo(
    () => (tgtEqpData || []).map((o) => ({ cmnCd: o.tgtEqpId, cmnCdNm: o.eqpNm } as Code)),
    [tgtEqpData]
  );

  const movePage = (viewType: ViewMode = 'month') => {
    setPageMode('calendar');
    setViewMode(viewType);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (cond?: PrjScheduleCondition) => {
    openLoading(true);
    try {
      const result = await findPrjScheduleList(cond ?? condition);
      setRowData(result || []);
    } finally {
      openLoading(false);
    }
  };

  const handleReset = () => {
    setCondition({ ...defaultCondition, elmPrjId: '' });
  };

  /** [신규] → 일정 등록 */
  const handleRegist = () => {
    setIsShowDetailPopup(true);
    setSelectedData({});
  };

  /** 일정 삭제 */
  const handleDelete = () => {
    const checkedIds = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem);
    if (checkedIds?.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.선택된 항목을 삭제하시겠습니까?', '선택된 항목을 삭제하시겠습니까?'),
      yesCallback: async () => {
        //
        deletePrjSchedule(checkedIds).then((res) => {
          if (res.successOrNot === 'Y') {
            openMessageBar({
              type: 'confirm',
              content: t('com.msg.삭제되었습니다', '삭제되었습니다.'),
            });
            handleSearch();
          }
        });
      },
    });
  };

  /** 행 더블클릭 → 일정 수정 */
  const handleModify = (item: PrjSchedule) => {
    setIsShowDetailPopup(true);
    setSelectedData(item);
  };

  /** 일정 등록/수정 팝업 닫기 */
  const closePopup = () => {
    setIsShowDetailPopup(false);
    setSelectedData({});
    handleSearch();
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 45,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'schdId',
        header: String(t('setup.label.일정ID', '일정ID')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트', '프로젝트')),
        width: 220,
      },
      {
        binding: 'wktTpCdNm',
        header: String(t('setup.label.업무유형', '업무유형')),
        align: 'center',
        width: 90,
      },
      {
        binding: 'schdNm',
        header: String(t('setup.label.제목', '제목')),
        width: '*',
        minWidth: 200,
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.schdNm}</span>',
          click: (e, ctx) => {
            handleModify(ctx.item);
          },
        }),
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체', '협력업체')),
        align: 'center',
        width: 130,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.대상설비', '대상설비')),
        width: 170,
      },
      {
        binding: 'planStDtm',
        header: String(t('setup.label.시작일자', '시작일자')),
        align: 'center',
        width: 100,
        cellTemplate: (grid) => (grid.value ? dayjs(String(grid.value)).format('YYYY-MM-DD') : ''),
      },
      {
        binding: 'planEndDtm',
        header: String(t('setup.label.종료일자', '종료일자')),
        align: 'center',
        width: 100,
        cellTemplate: (grid) => (grid.value ? dayjs(String(grid.value)).format('YYYY-MM-DD') : ''),
      },
      {
        binding: 'schdStatCdNm',
        header: String(t('setup.label.상태', '상태')),
        align: 'center',
        width: 80,
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.schdStatCd === 'CPLT') {
            return `<span class="green">${item.value}</span>`;
          } else if (item.item.schdStatCd === 'REG') {
            return `<span class="blue">${item.value}</span>`;
          } else {
            return `<span class="grey">${item.value}</span>`;
          }
        },
      },
      {
        binding: 'progMatrCtn',
        header: String(t('setup.label.진행사항', '진행사항')),
        width: 240,
      },
    ];
  }, [t]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    // 행 더블클릭 → 일정 수정 화면
    grid.hostElement.addEventListener('dblclick', (e) => {
      const ht = grid.hitTest(e);
      if (ht.cellType === 1 && ht.row >= 0) {
        const item = grid.rows[ht.row]?.dataItem as PrjSchedule;
        if (item?.schdId) handleModify(item);
      }
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.프로젝트', '프로젝트')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjMaster}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    if (ensolYn === 'Y') handleOnChange('excpId', '');
                    handleOnChange('tgtEqpId', '');
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.업무유형', '업무유형')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={wktTpCodes}
                  defaultValue={condition?.wktTpCd}
                  onChange={(value) => handleOnChange('wktTpCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.제목', '제목')}</label>
                <InputBox>
                  <CustomInputWithSearch
                    value={condition?.schdNm}
                    onChange={(e) => handleOnChange('schdNm', e.target.value)}
                    placeholder={String(
                      t('setup.label.제목을 입력해 주세요.', '제목을 입력해 주세요.')
                    )}
                  />
                </InputBox>
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체', '협력업체')}</label>
                {ensolYn === 'Y' ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={condition?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                      handleOnChange('tgtEqpId', '');
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{deptNm}</span>
                  </div>
                )}
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.대상설비', '대상설비')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsTgtEqp}
                  defaultValue={condition?.tgtEqpId}
                  onChange={(value) => handleOnChange('tgtEqpId', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.상태', '상태')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={statCodes}
                  defaultValue={condition?.schdStatCd}
                  onChange={(value) => handleOnChange('schdStatCd', value)}
                />
              </SearchCols>
              <SearchCols style={{ width: 'calc((100% - 72px) / 3)' }}>
                <label>{t('setup.label.기간', '기간')}</label>
                <CustomDateTimePicker
                  isRange={true}
                  value={[condition.planStDtm || '', condition.planEndDtm || '']}
                  onChange={([start, end]: [string, string]) => {
                    handleOnChange('planStDtm', start || '');
                    handleOnChange('planEndDtm', end || '');
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons onSearch={() => handleSearch()} onReset={handleReset} />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('pjm.label.일정 목록', '일정 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="icoNone"
            disableRipple
            onClick={() => movePage()}
          >
            {t('setup.label.일정 캘린더', '일정 캘린더')}
          </Button>
          <Button css={IconButton.button} className="plus" disableRipple onClick={handleRegist}>
            {t('com.button.일정 등록', '일정 등록')}
          </Button>
          <Button css={IconButton.button} className="minus" disableRipple onClick={handleDelete}>
            {t('com.button.일정 삭제', '일정 삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={520}
        isFilter={true}
        isReadOnly={true}
        selectionMode={6} // SelectionMode.Row
        initialized={onInitialized}
      />

      {isShowDetailPopup && (
        <PrjScheduleRegistPopup
          open={isShowDetailPopup}
          close={closePopup}
          schdId={selectedData.schdId}
        />
      )}
    </>
  );
};

export default PrjScheduleManagementPage;
